// ---------------------------------------------------------------------------
// Export / Import Pipeline — download & load editor state as Coogo JSON.
//
// exportProject()       — serialize editor state to a portable object.
// downloadProjectJson() — trigger a browser file download (.cogo.json).
// importProjectFromFile() — read a .cogo.json file and return a LoadedApp.
// openImportDialog()    — open file picker and import the selected file.
// ---------------------------------------------------------------------------

import type {
  CogoAppDef,
  CogoBlockDef,
  CogoEventBinding,
  CogoFlow,
  AppTarget,
  LoadedApp,
  DesignTokens,
} from '@/features/page-editor/store/types'
import { collectElementEvents } from '@cogo/types-v2'

/** Full export result */
export interface ExportResult {
  app: {
    id: string
    name: string
    appTarget: string
    pages: Array<{
      id: string
      route?: string
      title?: string
      blocks: string[]
    }>
  }
  blocks: Record<string, CogoBlockDef>
  data?: Record<string, unknown>
  flows?: Record<string, CogoFlow>
  tokens?: DesignTokens
}

/**
 * Export the full project — takes editor state, returns structured export.
 */
export function exportProject(state: {
  app: CogoAppDef | null
  blocks: Record<string, CogoBlockDef>
  appTarget: AppTarget
  data?: Record<string, unknown>
  flows?: Record<string, CogoFlow>
  tokens?: DesignTokens | null
}): ExportResult | null {
  const { app, blocks, appTarget, data, flows, tokens } = state
  if (!app) return null

  const result: ExportResult = {
    app: {
      id: app.appId,
      name: app.name,
      appTarget,
      pages: app.pages.map((p) => ({
        id: p.id,
        route: p.route,
        title: p.title,
        blocks: p.blocks,
      })),
    },
    blocks: { ...blocks },
  }

  // Include data, flows, tokens if they have content
  if (data && Object.keys(data).length > 0) result.data = data
  if (flows && Object.keys(flows).length > 0) result.flows = flows
  if (tokens) result.tokens = tokens

  return result
}

/**
 * Trigger a browser download of the project as a JSON file.
 */
export function downloadProjectJson(state: {
  app: CogoAppDef | null
  blocks: Record<string, CogoBlockDef>
  appTarget: AppTarget
  data?: Record<string, unknown>
  flows?: Record<string, CogoFlow>
  tokens?: DesignTokens | null
}): void {
  const result = exportProject(state)
  if (!result) return

  const json = JSON.stringify(result, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${result.app.name || 'project'}.cogo.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

/**
 * Parse an ExportResult JSON file into a LoadedApp for the store.
 */
export function importProjectFromJson(data: ExportResult): LoadedApp {
  if (!data.app || !data.blocks) {
    throw new Error('Invalid project file: missing "app" or "blocks"')
  }
  if (!data.app.id || !data.app.pages || !Array.isArray(data.app.pages)) {
    throw new Error('Invalid project file: app must have id and pages array')
  }

  // Reconstruct CogoAppDef from the export format
  const manifest: CogoAppDef = {
    appId: data.app.id,
    name: data.app.name || 'Imported Project',
    version: '1.0.0',
    schemaVersion: '2.0',
    theme: 'default',
    globalBlocks: [],
    pages: data.app.pages.map((p) => ({
      id: p.id,
      route: p.route ?? `/${p.id}`,
      title: p.title ?? p.id,
      blocks: p.blocks,
    })),
    datasources: {},
    globalState: {},
  } as unknown as CogoAppDef // TS2352 bridge — partial reconstruction from export shape; nested types not fully converted here

  return {
    manifest,
    blocks: data.blocks,
    data: data.data ?? {},
    flows: data.flows ?? {},
    tokens: data.tokens,
  }
}

/**
 * Read a File object, parse it as JSON, and convert to LoadedApp.
 */
export async function importProjectFromFile(file: File): Promise<LoadedApp> {
  const text = await file.text()
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error('Invalid JSON file')
  }
  return importProjectFromJson(parsed as ExportResult)
}

/**
 * Open a native file picker and import the selected .cogo.json file.
 * Returns the LoadedApp on success, or null if the user cancelled.
 */
export function openImportDialog(): Promise<LoadedApp | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json,.cogo.json'
    input.style.display = 'none'
    let settled = false

    const cleanup = () => {
      if (input.parentNode) document.body.removeChild(input)
      window.removeEventListener('focus', handleFocus)
    }

    input.addEventListener('change', async () => {
      settled = true
      const file = input.files?.[0]
      cleanup()
      if (!file) {
        resolve(null)
        return
      }
      try {
        const loaded = await importProjectFromFile(file)
        resolve(loaded)
      } catch (err) {
        reject(err)
      }
    })
    // Handle cancel (no file selected)
    input.addEventListener('cancel', () => {
      settled = true
      cleanup()
      resolve(null)
    })
    // Fallback: some browsers don't fire 'cancel' — clean up when window regains focus
    const handleFocus = () => {
      setTimeout(() => {
        if (!settled) {
          settled = true
          cleanup()
          resolve(null)
        }
      }, 300)
    }
    window.addEventListener('focus', handleFocus)

    document.body.appendChild(input)
    input.click()
  })
}

// ---------------------------------------------------------------------------
// Events serialization — extract element events into events.cogo.json format
// ---------------------------------------------------------------------------

/**
 * Extract all event bindings from a block into events.cogo.json format.
 *
 * Output format:
 * ```json
 * { "bindings": { "submit-btn:tap": { "flow": "submitForm" }, ... } }
 * ```
 *
 * The element-tree → flat-`bindings` walk is the shared, behavior-preserving
 * `collectElementEvents` transform in `@cogo/types-v2` (also used by the
 * cogo-page-editor app export), so the `${id}:${event}` key format and the
 * string-shorthand → `{ flow }` normalization stay in one place.
 */
export function extractBlockEvents(
  block: CogoBlockDef,
): { bindings: Record<string, CogoEventBinding> } {
  return { bindings: collectElementEvents(block.ui.root) }
}
